// mapApiErrorToFormErrors — implements frontend-design §4 Server-error
// mapping summary. Returns either field-level errors keyed by form
// field, or a form-level message, or a special "blocking" indication so
// the caller can render a blocking modal (BR-04, BR-07, BR-01, RC-1,
// G-14, activation, archive).

import { ApiError, getValidationField, getValidationFields } from '@/types/errors';

export type FormErrorMap = Record<string, string>;

export interface MappedFormError {
  // Field-level errors to splice into the form's error state.
  fieldErrors: FormErrorMap;
  // Form-level message (banner above submit). Mutually compatible with
  // fieldErrors — sometimes both are populated.
  formMessage: string | null;
  // If the error should open a blocking modal instead of (or in addition
  // to) form-level rendering, this names the modal.
  blockingModal:
    | null
    | 'duplicate_identity'
    | 'managed_credit_exceeds_100'
    | 'allocation_sum_mismatch'
    | 'last_admin_required'
    | 'person_owns_non_archived_agreements'
    | 'activation_blocked'
    | 'active_assignments_exist'
    | 'state_race';
  // If the AppShell or ErrorBoundary should handle this (401 → re-auth,
  // 403 → close form, etc.).
  appLevel: 'unauthenticated' | 'forbidden' | null;
  // Forwarded for support correlation.
  requestId: string | undefined;
}

export function mapApiErrorToFormErrors(
  err: unknown,
  knownFields: readonly string[],
): MappedFormError {
  const empty: MappedFormError = {
    fieldErrors: {},
    formMessage: null,
    blockingModal: null,
    appLevel: null,
    requestId: undefined,
  };
  if (!(err instanceof ApiError)) {
    return {
      ...empty,
      formMessage:
        err instanceof Error ? err.message : 'Something went wrong.',
    };
  }
  const out: MappedFormError = { ...empty, requestId: err.requestId };
  switch (err.code) {
    case 'unauthenticated':
      out.appLevel = 'unauthenticated';
      return out;
    case 'forbidden': {
      const reason = (err.details['reason'] as string | undefined) ?? null;
      if (reason === 'completed_state') {
        out.formMessage = 'Only End Date is editable on a completed agreement.';
      } else if (reason === 'archived_state') {
        out.formMessage = 'Archived agreements are read-only.';
      } else if (reason === 'ownership') {
        out.formMessage = "You can only perform this action on agreements you own.";
      } else {
        out.formMessage = "You don't have permission to do that.";
      }
      out.appLevel = 'forbidden';
      return out;
    }
    case 'not_found': {
      // 404 on the entity itself: form-level. 422-with-field shape:
      // field-level. Distinguish by status.
      const field = getValidationField(err);
      if (err.status === 422 && field && knownFields.includes(field)) {
        out.fieldErrors[field] = 'This selection no longer exists. Pick another.';
        return out;
      }
      out.formMessage = 'This record no longer exists.';
      return out;
    }
    case 'validation_failed': {
      const single = getValidationField(err);
      if (single && knownFields.includes(single)) {
        out.fieldErrors[single] = err.message || 'Invalid value.';
        return out;
      }
      const list = getValidationFields(err);
      if (list.length) {
        for (const f of list) {
          if (knownFields.includes(f.field)) {
            out.fieldErrors[f.field] = f.message;
          }
        }
        if (Object.keys(out.fieldErrors).length === 0) {
          out.formMessage = err.message || 'Some fields are invalid.';
        }
        return out;
      }
      out.formMessage = err.message || 'Some fields are invalid.';
      return out;
    }
    case 'end_date_before_start_date':
      if (knownFields.includes('end_date')) {
        out.fieldErrors['end_date'] =
          'End date must be on or after the start date.';
      } else {
        out.formMessage = 'End date must be on or after the start date.';
      }
      return out;
    case 'cannot_assign_member_as_owner':
      if (knownFields.includes('owner_person_id')) {
        out.fieldErrors['owner_person_id'] =
          'Owner must be a Practice Lead or Admin.';
      } else {
        out.formMessage = 'Owner must be a Practice Lead or Admin.';
      }
      return out;
    case 'duplicate_identity':
      out.blockingModal = 'duplicate_identity';
      out.formMessage =
        'An item with this identity already exists.';
      return out;
    case 'managed_credit_exceeds_100':
      out.blockingModal = 'managed_credit_exceeds_100';
      return out;
    case 'allocation_sum_mismatch':
      out.blockingModal = 'allocation_sum_mismatch';
      return out;
    case 'last_admin_required':
      out.blockingModal = 'last_admin_required';
      return out;
    case 'person_owns_non_archived_agreements':
      out.blockingModal = 'person_owns_non_archived_agreements';
      return out;
    case 'incomplete_sales_credit':
    case 'payment_schedule_mismatch':
    case 'no_assignments':
    case 'activation_blocked':
      out.blockingModal = 'activation_blocked';
      return out;
    case 'active_assignments_exist':
      out.blockingModal = 'active_assignments_exist';
      return out;
    case 'invalid_state_transition':
    case 'irreversible_status':
      out.blockingModal = 'state_race';
      return out;
    case 'immutable_field':
      out.formMessage =
        'Internal error — please refresh and try again.';
      return out;
    case 'unsupported_idempotency_key':
    case 'unsupported_media_type':
    case 'request_too_large':
      out.formMessage = 'Internal error — please refresh and try again.';
      return out;
    case 'service_unavailable':
      out.formMessage = "Couldn't reach the server. Please try again.";
      return out;
    case 'internal_error':
      out.formMessage = `Something went wrong${
        err.requestId ? ` (ref: ${err.requestId})` : ''
      }. Please try again.`;
      return out;
    case 'network_error':
      out.formMessage = "Couldn't reach the server. Check your connection and try again.";
      return out;
    default:
      out.formMessage = err.message || 'Something went wrong.';
      return out;
  }
}
